import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react'
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { channelsAPI } from '../services/api'
import { toast } from '../services/toastService'
import { useChannelList } from '../hooks/useChannelList'
import { useChannelMessages } from '../hooks/useChannelMessages'
import { useChannelAutoSelect } from '../hooks/useChannelAutoSelect'
import { useChannelNotifications } from '../hooks/useChannelNotifications'
import { useChannelRateLimit } from '../hooks/useChannelRateLimit'
import { useMessageActions } from '../hooks/useMessageActions'
import { useContactFormSubmission } from '../hooks/useContactFormSubmission'
import { useChannelMessageSend } from '../hooks/useChannelMessageSend'
import useChannelWebSocket from '../hooks/useChannelWebSocket'
import { useTranslation } from 'react-i18next'
import ChannelsSidebar from './channels/ChannelsSidebar'
import ChannelHeader from './channels/ChannelHeader'
import MessageList from './channels/MessageList'
import MessageInput from './channels/MessageInput'
import ContactFormPanel from './channels/ContactFormPanel'
import AuthenticatedContactForm from './channels/AuthenticatedContactForm'
import { createLogger } from '../utils/logger'
import { useChannelViewEffects } from '../hooks/useChannelViewEffects'

const logger = createLogger('Channels')

/**
 * CompactChannelsView - Main channels interface component
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * 1. React 18 automatic batching groups state updates (setMessages + setChannels) into single render
 * 2. Custom memo comparison in ChannelListItem prevents unnecessary child re-renders
 * 3. All callbacks are memoized with useCallback
 * 4. Filtered channels computed with useMemo
 * 5. Deep comparison in useChannelList prevents updates when data unchanged
 * 
 * Result: Reduced re-renders from ~6 to ~2-3 when switching channels
 * 
 */

const CompactChannelsView = memo(function CompactChannelsView({ user, onTabChange, initialChannelId, onChannelSelected, onUnreadCountChange, claimPrefill, onClaimPrefillConsumed }) {
  const { t } = useTranslation()
  
  // Declare auth state early since it's needed by hooks below
  const isAuthenticated = !!user
  const isAdmin = user?.isAdmin || false
  
  // Channel list management hook (replaces inline fetchChannels + state)
  const { 
    channels, 
    setChannels, 
    activeChannel, 
    setActiveChannel, 
    fetchChannels
    // Note: loading from useChannelList is not used in render, omitted to prevent unnecessary re-renders
  } = useChannelList(isAuthenticated, isAdmin, user, t, initialChannelId)
  
  // Message management hook with pagination (replaces inline message logic)
  const {
    messages,
    setMessages,
    fetchMessages,
    loadMoreMessages,
    hasMoreMessages,
    loadingOlderMessages
  } = useChannelMessages(isAuthenticated)
  
  // Contact form submission hook
  const {
    contactFormData,
    isVendor,
    contactSuccess,
    contactRateLimitTime,
    sending: contactSending,
    setIsVendor,
    setContactSuccess,
    setContactRateLimitTime,
    setContactFormData,
    handleContactChange,
    handleContactSubmit,
    checkExistingRateLimit
  } = useContactFormSubmission(t)
  
  // Only check rate limits for authenticated users with real channels (not mock channels)
  const { canPost, nextPostTime, checkRateLimit } = useChannelRateLimit(
    isAuthenticated && activeChannel?.id !== 'mock-support' ? activeChannel?.id : null
  )
  
  // Message sending hook
  const { newMessage, setNewMessage, sending, handleSendMessage } = useChannelMessageSend(
    isAuthenticated,
    activeChannel,
    canPost,
    checkRateLimit,
    fetchChannels,
    t
  )
  
  // Local UI state
  const [showOnlyNeedsReply, setShowOnlyNeedsReply] = useState(false)
  const [showChannelsList, setShowChannelsList] = useState(false)
  const [showContactForm, setShowContactForm] = useState(false)
  const [activeTab, setActiveTab] = useState('user') // 'user' or 'admin'
  const [brokenAvatars, setBrokenAvatars] = useState(new Set())
  const messagesEndRef = useRef(null)
  const lastProcessedChannelRef = useRef(null)
  const lastFetchedChannelRef = useRef(null)
  const hasAutoSelectedTabRef = useRef(false)
  
  // Callback to mark when auto-select has set the tab
  const handleAutoSelectComplete = useCallback((channel) => {
    hasAutoSelectedTabRef.current = true
    if (onChannelSelected) {
      onChannelSelected(channel)
    }
  }, [onChannelSelected])
  
  // Auto-select hook (replaces inline progressive retry logic)
  useChannelAutoSelect(channels, initialChannelId, activeChannel, setActiveChannel, setActiveTab, handleAutoSelectComplete)
  
  // Desktop notifications hook (replaces inline notification logic)
  useChannelNotifications(channels, activeChannel, isAuthenticated, isAdmin, user, t)

  // Seed form when an unauthenticated user arrives via "Claim this business"
  useEffect(() => {
    if (!claimPrefill || isAuthenticated) return
    setIsVendor(true)
    setContactFormData(prev => ({
      ...prev,
      entityId: claimPrefill.entityId || '',
      entityName: claimPrefill.entityName || '',
      vendorIntent: 'claim'
    }))
    setShowContactForm(true)
    setShowChannelsList(false)
    if (onClaimPrefillConsumed) onClaimPrefillConsumed()
  }, [claimPrefill]) // eslint-disable-line react-hooks/exhaustive-deps

  // WebSocket callback for real-time messages
  // Optimized: React 18 automatically batches these updates
  const handleWebSocketMessage = useCallback((messageData) => {
    logger.info('Received WebSocket message', { messageData })
    
    // Defensive check: ensure messageData exists and has required fields
    if (!messageData || !messageData.id) {
      logger.warn('Received invalid WebSocket message', { messageData })
      return
    }
    
    const currentChannelId = activeChannel?.id
    
    // Add the new message to the messages array
    setMessages(prev => {
      // Check if message already exists (prevent duplicates)
      const exists = prev.some(msg => msg.id === messageData.id)
      if (exists) return prev
      return [...prev, messageData]
    })
    
    // Update local channel state (mark as read if viewing this channel)
    // React 18 will batch this with setMessages above automatically
    if (currentChannelId) {
      setChannels(prev => {
        // Only update if unreadCount is not already 0 (avoid unnecessary array creation)
        // Treat undefined/null as 0
        const needsUpdate = prev.some(ch => ch.id === currentChannelId && (ch.unreadCount || 0) !== 0)
        if (!needsUpdate) return prev
        
        return prev.map(ch => 
          ch.id === currentChannelId ? { ...ch, unreadCount: 0 } : ch
        )
      })
      
      // Mark channel as read on server (async, doesn't block)
      channelsAPI.markChannelAsRead(currentChannelId).catch(err => {
        logger.error('Failed to mark channel as read', err, { channelId: currentChannelId })
      })
    }
    
    // Always notify parent to refresh unread count (even if not viewing specific channel)
    // This ensures notification badge updates immediately
    if (onUnreadCountChange) {
      onUnreadCountChange()
    }
  }, [activeChannel?.id, onUnreadCountChange, setChannels])
  
  // WebSocket connection for real-time messaging (replaces 30s polling)
  // Use ref to ensure we always connect to the current channel (avoids stale closure issues during auto-select)
  const { connectionStatus, isConnected } = useChannelWebSocket(
    isAuthenticated && activeChannel?.id && activeChannel?.id !== 'mock-support' ? activeChannel.id : null,
    handleWebSocketMessage,
    (error) => logger.error('WebSocket error', error)
  )

  // Message actions (edit/delete)
  const {
    editingMessage,
    editContent,
    setEditContent,
    startEdit,
    cancelEdit,
    handleEdit,
    handleDelete
  } = useMessageActions(activeChannel?.id)

  const isSupportChannel = activeChannel?.name === 'TrckIt Support'
  const hasNoMessages = messages.length === 0 || (messages.length === 1 && messages[0].userName !== user?.firstName + ' ' + user?.lastName)

  // Handle message edit
  const handleEditMessage = useCallback((message) => {
    startEdit(message)
  }, [startEdit])

  const handleSaveEdit = useCallback(async (messageId) => {
    const result = await handleEdit(messageId, editContent, (msgId, newContent) => {
      // Update message in local state
      setMessages(prev => prev.map(msg => 
        msg.id === msgId 
          ? { ...msg, content: newContent, isEdited: true, editedAt: new Date().toISOString() }
          : msg
      ))
      toast.success(t('channels.message.editSuccess'))
    })

    if (!result.success && result.error) {
      toast.error(result.error)
    }
  }, [handleEdit, editContent, t])

  const handleCancelEdit = useCallback(() => {
    cancelEdit()
  }, [cancelEdit])

  const handleDeleteMessage = useCallback(async (messageId) => {
    const result = await handleDelete(messageId, (msgId) => {
      // Remove message from local state
      setMessages(prev => prev.filter(msg => msg.id !== msgId))
      toast.success(t('channels.message.deleteSuccess'))
    })

    if (!result.success && result.error) {
      toast.error(result.error)
    }
  }, [handleDelete, t])

  // Clear broken avatar cache on channel switch so transient load failures don't persist
  useEffect(() => {
    setBrokenAvatars(new Set())
  }, [activeChannel?.id])

  // Side effects consolidated in custom hook
  useChannelViewEffects({
    user, isAuthenticated, isAdmin, channels, activeChannel, messages,
    messagesEndRef, lastProcessedChannelRef, lastFetchedChannelRef, hasAutoSelectedTabRef,
    isSupportChannel, hasNoMessages, onUnreadCountChange,
    setMessages, setNewMessage, setShowContactForm, setActiveTab, setContactRateLimitTime,
    setChannels, fetchChannels, fetchMessages,
  })

  // Helper function to get display name for a channel
  const getChannelDisplayName = useCallback((ch) => {
    // For admin viewing OTHER users' support channels (ownerEmail is only populated for admins)
    if (isAdmin && ch.channelType === 'default' && ch.ownerEmail && ch.ownerEmail !== user?.email) {
      return `${ch.ownerFirstName} ${ch.ownerLastName} (${ch.ownerEmail})`
    }
    
    // For admin viewing support channels with no owner data (orphaned channels from seed data)
    // Show last message user if available, otherwise show it's orphaned
    if (isAdmin && ch.channelType === 'default' && !ch.ownerEmail) {
      if (ch.lastMessageUser && ch.lastMessageUser.trim()) {
        return t('channels.orphanedUserSupport', "{{name}}'s Support", { name: ch.lastMessageUser })
      }
      // No owner data and no messages - this is an orphaned channel
      return t('channels.orphanedNoUser', 'Support Channel (No User)')
    }
    
    // Translate standard support channel name
    if (ch.name === 'TrckIt Support') {
      return t('channels.supportChannelName')
    }
    return ch.name
  }, [isAdmin, user?.email, t])

  // Split channels into user channels and support channels - memoized to prevent infinite loops
  // User channels (My Channels) = entity channels only
  // Support channels = all default support channels (user's own + others for admin)
  const userChannels = useMemo(() => {
    return channels.filter(ch => ch.channelType === 'entity')
  }, [channels])
  
  const supportChannels = useMemo(() => {
    return channels.filter(ch => ch.channelType === 'default')
  }, [channels])

  // Filter channels based on active tab - memoized
  const filteredChannels = useMemo(() => {
    if (activeTab === 'user') {
      return userChannels
    } else if (activeTab === 'admin') {
      if (showOnlyNeedsReply) {
        return supportChannels.filter(ch => ch.needsReply)
      }
      return supportChannels
    }
    return []
  }, [activeTab, userChannels, supportChannels, showOnlyNeedsReply])

  const totalUnread = useMemo(() => {
    return channels.reduce((sum, ch) => sum + (ch.unreadCount || 0), 0)
  }, [channels])
  const needsReplyCount = useMemo(() => supportChannels.filter(ch => ch.needsReply).length, [supportChannels])

  // Sidebar callbacks (must be before early returns per Rules of Hooks)
  const handleSelectChannel = useCallback((ch) => {
    setActiveChannel(ch)
    // Close the sidebar on mobile/small screens only (< 768px)
    // On larger screens, keep the sidebar visible
    if (window.innerWidth < 768) {
      setShowChannelsList(false)
    }
    // Clear unread badge immediately when channel is clicked
    if (ch.unreadCount > 0) {
      setChannels(prev => {
        // Find channel and only update if needed
        // Treat undefined/null unreadCount as 0
        const channel = prev.find(c => c.id === ch.id)
        if (!channel || (channel.unreadCount || 0) === 0) return prev
        
        return prev.map(c => 
          c.id === ch.id ? { ...c, unreadCount: 0 } : c
        )
      })
      // Notify parent to refresh global unread count immediately
      if (onUnreadCountChange) {
        onUnreadCountChange()
      }
    }
  }, [onUnreadCountChange, setChannels])
  
  const handleToggleNeedsReply = useCallback(() => {
    setShowOnlyNeedsReply(prev => !prev)
  }, [])

  // Update browser tab title with unread count
  useEffect(() => {
    const baseTitle = 'TrckIt'
    if (totalUnread > 0) {
      document.title = `(${totalUnread}) ${baseTitle}`
    } else {
      document.title = baseTitle
    }
  }, [totalUnread])

  // Note: Removed loading spinner check - component renders normally during background fetching
  // This prevents unnecessary re-renders every 30 seconds from useChannelList's loading state

  if (channels.length === 0 && isAuthenticated) {
    return (
      <div className="text-center py-12 px-6">
        <ChatBubbleLeftRightIcon className="w-14 h-14 mx-auto mb-3 text-gray-300" />
        <p className="text-sm font-medium text-gray-700">{t('channels.noChannels')}</p>
        <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">{t('channels.noChannelsCTA')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-1 rounded-lg border border-gray-200/50 shadow-lg overflow-hidden">
      {/* Channels Sidebar */}
      <ChannelsSidebar
        showChannelsList={showChannelsList}
        activeTab={activeTab}
        isAdmin={isAdmin}
        userChannels={userChannels}
        supportChannels={supportChannels}
        showOnlyNeedsReply={showOnlyNeedsReply}
        filteredChannels={filteredChannels}
        activeChannel={activeChannel}
        needsReplyCount={needsReplyCount}
        onToggleSidebar={setShowChannelsList}
        onSwitchTab={setActiveTab}
        onToggleNeedsReply={handleToggleNeedsReply}
        onSelectChannel={handleSelectChannel}
        getChannelDisplayName={getChannelDisplayName}
      />
      
      {/* Main Content Area */}
      <div className="flex-1 min-w-0 flex flex-col bg-gray-50/50">
        {/* Header */}
        <ChannelHeader
          activeChannel={activeChannel}
          getChannelDisplayName={getChannelDisplayName}
          isAuthenticated={isAuthenticated}
          connectionStatus={connectionStatus}
        />

        {/* Messages Area or Contact Form */}
        {showContactForm && !isAuthenticated ? (
          /* Contact form for non-authenticated users - handled by ContactFormPanel */
          <ContactFormPanel
            isAuthenticated={isAuthenticated}
            contactSuccess={contactSuccess}
            contactRateLimitTime={contactRateLimitTime}
            contactFormData={contactFormData}
            isVendor={isVendor}
            isVendorLocked={isVendor && !!contactFormData.entityId}
            sending={contactSending}
            newMessage={newMessage}
            onContactChange={handleContactChange}
            onContactSubmit={handleContactSubmit}
            onSendMessage={handleSendMessage}
            onSetIsVendor={setIsVendor}
            onSetContactSuccess={setContactSuccess}
            onSetNewMessage={setNewMessage}
            onTabChange={onTabChange}
          />
        ) : showContactForm ? (
          /* Simplified form for authenticated users */
          <AuthenticatedContactForm
            newMessage={newMessage}
            sending={sending}
            onMessageChange={setNewMessage}
            onSubmit={handleSendMessage}
          />
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex flex-col flex-1 overflow-y-auto min-h-0">
              <div className="flex-1" />
              <MessageList 
                messages={messages}
                messagesEndRef={messagesEndRef}
                brokenAvatars={brokenAvatars}
                onAvatarError={(avatar) => setBrokenAvatars(prev => new Set(prev).add(avatar))}
                currentUserId={user?.id}
                isAdmin={isAdmin}
                onEditMessage={handleEditMessage}
                onDeleteMessage={handleDeleteMessage}
                editingMessage={editingMessage}
                editContent={editContent}
                onEditContentChange={setEditContent}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                hasMoreMessages={hasMoreMessages}
                loadingOlderMessages={loadingOlderMessages}
                onLoadMore={loadMoreMessages}
              />
            </div>

            <MessageInput
              newMessage={newMessage}
              canPost={canPost}
              rateLimitInfo={nextPostTime ? { nextPostAt: nextPostTime } : null}
              sending={sending}
              onMessageChange={setNewMessage}
              onSubmit={handleSendMessage}
            />
          </div>
        )}
      </div>
    </div>
  )
})

export default CompactChannelsView
